import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as NeutralIcon,
} from '@mui/icons-material';

interface MetricCardProps {
  title: string;
  value: number;
  unit?: string;
  trend?: number;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit = '',
  trend,
  color = 'primary',
}) => {

  const getTrendIcon = () => {
    if (!trend) return undefined;
    if (trend > 0) return <TrendingUpIcon fontSize="small" />;
    if (trend < 0) return <TrendingDownIcon fontSize="small" />;
    return <NeutralIcon fontSize="small" />;
  };

  const getTrendColor = () => {
    if (!trend) return 'default';
    if (trend > 0) return 'success';
    if (trend < 0) return 'error';
    return 'default';
  };

  return (
    <Card>
      <CardContent>
        <Typography color="textSecondary" gutterBottom variant="body2">
          {title}
        </Typography>
        <Typography variant="h4" component="div">
          {value}
          {unit && (
            <Typography
              component="span"
              variant="body1"
              color="textSecondary"
              style={{ marginLeft: 8 }}
            >
              {unit}
            </Typography>
          )}
        </Typography>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
            <Chip
              icon={getTrendIcon()}
              label={`${trend > 0 ? '+' : ''}${trend}%`}
              size="small"
              color={getTrendColor() as any}
              variant="outlined"
            />
          </div>
        )}
        <LinearProgress
          variant="determinate"
          value={Math.min(100, value)}
          color={color}
          style={{ marginTop: 16, height: 8, borderRadius: 4 }}
        />
      </CardContent>
    </Card>
  );
};

interface FocusMetricsProps {
  focusScore: number;
  deepWorkHours: number;
  interruptionCount: number;
  productivityIndex: number;
  trends?: {
    focus?: number;
    deepWork?: number;
    interruptions?: number;
    productivity?: number;
  };
}

const FocusMetrics: React.FC<FocusMetricsProps> = ({
  focusScore,
  deepWorkHours,
  interruptionCount,
  productivityIndex,
  trends = {},
}) => {
  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Focus Metrics
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Focus Score"
            value={focusScore}
            unit="%"
            trend={trends.focus}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Deep Work"
            value={deepWorkHours}
            unit="hrs"
            trend={trends.deepWork}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Interruptions"
            value={interruptionCount}
            trend={trends.interruptions}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Productivity"
            value={productivityIndex}
            unit="%"
            trend={trends.productivity}
            color="info"
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default FocusMetrics;